/**
 * Removes all empty values from an object or array, including nested structures.
 * Modifies the input object in place and returns it.
 * Non-object values (primitives, null) are returned unchanged.
 *
 * @template T - The type of the input object or value.
 * @param obj - The input object, array, or value to process.
 * @returns The input with all `null` values removed, preserving the original type.
 * @example
 * ```typescript
 * const data = { a: null, b: 2, c: { d: null, e: 3 } };
 * removeEmpty(data); // Returns { b: 2, c: { e: 3 } }
 * ```
 */
export function removeEmpty<T>(obj: T): T {
    if (Array.isArray(obj)) {
      return obj
        .map((item) => removeEmpty(item))
        .filter((v) => v !== null) as T;
    }
  
    if (typeof obj !== 'object' || obj === null) {
      return obj;
    }
  
    Object.keys(obj).forEach((key) => {
      if (obj[key] === null) {
        delete obj[key];
        return;
      }
      obj[key] = removeEmpty(obj[key]);
    });
  
    return obj;
}

export function removeNullAndUndefined<T>(obj: T): Partial<T> {
  if (obj === null || obj === undefined) {
    return {} as Partial<T>;
  }

  if (Array.isArray(obj)) {
    return obj
      .map((item) => removeNullAndUndefined(item))
      .filter((v) => v !== null && v !== undefined) as unknown as Partial<T>;
  }

  if (typeof obj !== 'object') {
    return obj as Partial<T>;
  }

  const cleaned = Object.fromEntries(
    Object.entries(obj)
      .map(([key, value]) => {
        if (value !== null && value !== undefined && typeof value === 'object' && !Array.isArray(value)) {
          const cleanedValue = removeNullAndUndefined(value);
          if (Object.keys(cleanedValue).length === 0) {
            return null;
          }
          return [key, cleanedValue];
        }
        return [key, value];
      })
      .filter(([_, value]) => value !== null && value !== undefined)
  ) as Partial<T>;

  return cleaned;
}